import { ScreenBase } from "@frui.ts/screens/dist";
import { action, computed, observable, reaction } from "mobx";
import { interfaces } from "inversify";
import ChargeOrder from "models/chargeOrder";
import ChargeOrderRepository from "repositories/chargeOrderRepository";
import type PayGatePaymentResponse from "models/payGatePaymentResponse";
import { PayGatePaymentStatus, PayGatePaymentType } from "models/payGatePaymentResponse";
import LocalizationService from "services/localizationService";
import ChargeTypesService from "services/chargeTypesService";
import EnumService from "services/enumService";
import PriceService from "services/priceService";
import TimeService from "services/timeService";
import FuelType from "entities/fuelType";
import ChargeValidity from "entities/chargeValidity";
import GooglePayMetadata from "models/googlePayMetadata";
import PaymentService from "services/payment/paymentService";
import EventBus, {
  getGooglePayMetadataFailure,
  getGooglePayMetadataSuccess,
  googlePayPaymentBrowserFingerprintLoaded,
  submitGooglePayPaymentBrowserFingerprint,
  submitGooglePayPaymentDeferred,
  submitGooglePayPaymentFailure,
  submitGooglePayPaymentSuccess,
} from "services/eventBus";

export const PaymentRetryNavigationName = "retry";

const RETRY_CONSUMED_PREFIX = "retry_consumed_";

if (typeof window !== "undefined" && typeof performance !== "undefined" && performance.getEntriesByType) {
  const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
  let isLangChange = false;
  try {
    isLangChange = sessionStorage.getItem("lang-change") === "1";
  } catch {
    /* ignore */
  }

  if (nav?.type === "reload" || isLangChange) {
    try {
      sessionStorage.removeItem("lang-change");
      Object.keys(sessionStorage)
        .filter(k => k.startsWith(RETRY_CONSUMED_PREFIX))
        .forEach(k => sessionStorage.removeItem(k));
    } catch {
      /* ignore */
    }
  } else if (nav?.type === "navigate") {
    try {
      const hash = window.location.hash;
      const retryIdx = hash.indexOf(`/${PaymentRetryNavigationName}/`);
      if (retryIdx !== -1) {
        const afterRetry = hash.slice(retryIdx + `/${PaymentRetryNavigationName}/`.length);
        const orderId = afterRetry.split(/[/?#]/)[0];
        if (orderId) {
          clearRetryConsumed(orderId);
        }
      } else {
      }
    } catch {
      /* ignore */
    }
  }
}

// Permanent popstate guard — intercepts browser Back/Forward to a consumed retry
// URL regardless of which widget is currently active. Replaces the retry entry in
// history with the parent module's base path and re-triggers hashchange so the
// frui.ts router navigates to the appropriate default screen.
if (typeof window !== "undefined") {
  window.addEventListener("popstate", () => {
    const hash = window.location.hash;
    const retryIdx = hash.indexOf(`/${PaymentRetryNavigationName}/`);
    if (retryIdx === -1) return;
    const afterRetry = hash.slice(retryIdx + `/${PaymentRetryNavigationName}/`.length);
    const orderId = afterRetry.split(/[/?#]/)[0];
    if (!orderId || !isRetryBlocked(orderId)) return;

    // Replace the blocked retry URL with the parent module's base hash
    // (e.g. "#/eshop" or "#/multi_eshop") so it disappears from history.
    const baseHash = hash.slice(0, retryIdx) || "#/";
    window.history.replaceState(null, "", baseHash);
    // Re-trigger routing so frui.ts navigates to the default child screen.
    window.dispatchEvent(new Event("hashchange"));
  });
}

export function markRetryConsumed(orderId?: string) {
  if (!orderId) return;
  try {
    sessionStorage.setItem(`${RETRY_CONSUMED_PREFIX}${orderId}`, "1");
  } catch {
    /* ignore */
  }
}

export function clearRetryConsumed(orderId?: string) {
  if (!orderId) return;
  try {
    sessionStorage.removeItem(`${RETRY_CONSUMED_PREFIX}${orderId}`);
  } catch {
    /* ignore */
  }
}

export function isRetryBlocked(orderId?: string): boolean {
  if (!orderId) return false;
  try {
    return sessionStorage.getItem(`${RETRY_CONSUMED_PREFIX}${orderId}`) === "1";
  } catch {
    return false;
  }
}

export default class PaymentRetryViewModel extends ScreenBase {
  readonly IDLE_TIMER_DELAY = 1000 * 150; // Time before countdown tooltip is shown (150 seconds)
  readonly IDLE_TIMER_TIMEOUT = 1000 * 270; // Time when idle modal is shown (270 seconds)
  readonly IDLE_PROMPT_TIMEOUT = 1000 * 30; // Time in modal before form deletion (30 seconds) - Total: 300s
  readonly IDLE_TIMER_DEBOUNCE = 250;

  @observable isLoading = false;
  @observable payGatePaymentResponse?: PayGatePaymentResponse;
  @observable retryBlocked = false;
  @observable googlePayMetadata: GooglePayMetadata | undefined;
  @observable googlePayPaymentFingerprint?: string;
  @observable googlePayFingerprintProcessed = false;
  @observable showIdleModal = false;
  @observable counter: number;
  private intervalId?: any;
  private unsubscriptions: Array<() => void> = [];

  constructor(
    public chargeOrder: ChargeOrder,
    public localizationService: LocalizationService,
    public timeService: TimeService,
    private chargeOrderRepository: ChargeOrderRepository,
    private chargeTypesService: ChargeTypesService,
    private enumService: EnumService,
    private priceService: PriceService,
    private paymentService: PaymentService,
    private eventBus: EventBus,
    private onRetryPayment: () => void,
    private onBackToPurchase: () => void,
    private onPaymentSucceeded: () => void,
    private onPaymentInProgress: () => void,
    private onRetryGooglePayPayment?: (paymentData: google.payments.api.PaymentData) => Promise<void> | void,
    private onIdleTimeoutCallback?: () => void
  ) {
    super();
    this.navigationName = `${PaymentRetryNavigationName}/${this.chargeOrder.id}`;
    this.counter = this.IDLE_PROMPT_TIMEOUT;
    this.ensureInitialized();
    this.unsubscriptions.push(
      this.eventBus.subscribe(getGooglePayMetadataSuccess, this.receivedGooglePayMetadata),
      this.eventBus.subscribe(getGooglePayMetadataFailure, this.googlePayMetadataFetchFailure),
      this.eventBus.subscribe(submitGooglePayPaymentBrowserFingerprint, this.googlePayPaymentBrowserFingerprint),
      this.eventBus.subscribe(submitGooglePayPaymentFailure, this.googlePayFingerprintReset),
      this.eventBus.subscribe(submitGooglePayPaymentSuccess, this.onSubmitGooglePayPaymentSuccess),
      this.eventBus.subscribe(submitGooglePayPaymentDeferred, this.onSubmitGooglePayPaymentDeferred),
      reaction(
        () => this.isGooglePayAllowed,
        allowed => allowed && this.ensureGooglePayMetadata(),
        { fireImmediately: true }
      )
    );
  }

  protected async onActivate(): Promise<void> {
    // When activated via the URL (refresh / lang switch / direct nav), the
    // payGatePayment response may not be set yet — fetch it once so the view
    // can render the retry buttons.
    if (!this.payGatePaymentResponse) {
      await this.fetchPayGatePayment();
    }
    // Tag this retry as consumed when the page is unloaded (external link click,
    // full navigation away). Refresh (F5) and language change also fire pagehide
    // but are exempted: F5 via nav.type==="reload", language change via the
    // "lang-change" sessionStorage flag set in app.tsx before the reload.
    const onPageHide = () => {
      let isLangChange = false;
      try {
        isLangChange = sessionStorage.getItem("lang-change") === "1";
      } catch {
        /* ignore */
      }
      if (!isLangChange) {
        markRetryConsumed(this.chargeOrder.id);
      }
    };
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted && isRetryBlocked(this.chargeOrder.id)) {
        const currentHash = window.location.hash;
        const retryPath = `/${PaymentRetryNavigationName}/${this.chargeOrder.id}`;
        const idx = currentHash.indexOf(retryPath);
        const baseHash = idx !== -1 ? currentHash.slice(0, idx) : "#/";
        window.history.replaceState(null, "", baseHash || "#/");
        window.dispatchEvent(new Event("hashchange"));
      }
    };
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);
    this.unsubscriptions.push(
      () => window.removeEventListener("pagehide", onPageHide),
      () => window.removeEventListener("pageshow", onPageShow)
    );
  }

  protected async onDeactivate(isClosing: boolean): Promise<void> {
    let isLangChange = false;
    try {
      isLangChange = sessionStorage.getItem("lang-change") === "1";
    } catch {
      /* ignore */
    }

    if (!isLangChange) {
      markRetryConsumed(this.chargeOrder.id);
      const retryPath = `/${PaymentRetryNavigationName}/${this.chargeOrder.id}`;
      const currentHash = window.location.hash;
      const idx = currentHash.indexOf(retryPath);
      if (idx !== -1) {
        window.history.replaceState(null, "", currentHash.slice(0, idx) || window.location.pathname);
      }
    }

    this.clearTimeoutInterval();

    if (isClosing) {
      this.unsubscriptions.forEach(u => u());
      this.unsubscriptions = [];
    }
    await super.onDeactivate(isClosing);
  }

  @action.bound
  private async ensureInitialized() {
    if (!this.chargeTypesService.isInitialized) {
      this.isLoading = true;
      await this.chargeTypesService.initialize(false);
      this.isLoading = false;
    }
  }

  @computed
  get isMultiple() {
    return this.chargeOrder._origin === "multiple";
  }

  @computed
  get isDifferentTimeZone() {
    return this.timeService.isFetched && !this.timeService.isServerTimeSame;
  }

  @computed
  get isRetryAllowed() {
    return this.payGatePaymentResponse?.permittedActions?.paymentRetry?.isAllowed ?? false;
  }

  @computed
  get isPaymentInProgress() {
    return this.payGatePaymentResponse?.status === PayGatePaymentStatus.IN_PROGRESS;
  }

  @computed
  get allowedPaymentTypes(): PayGatePaymentType[] {
    return this.payGatePaymentResponse?.permittedActions?.paymentRetry?.allowedPayGatePaymentTypes ?? [];
  }

  @computed
  get isGooglePayAllowed() {
    return (
      !this.retryBlocked && !!this.onRetryGooglePayPayment && this.allowedPaymentTypes.includes(PayGatePaymentType.GOOGLE_PAY)
    );
  }

  @computed
  get isCardPayAllowed() {
    return !this.retryBlocked && this.allowedPaymentTypes.includes(PayGatePaymentType.STANDARD);
  }

  @computed
  get isGooglePayMetadataReady() {
    return !this.paymentService.isFetchingGooglePayMetadata && this.googlePayMetadata !== undefined;
  }

  @computed
  get grandTotalRawForGooglePay() {
    return (
      this.chargeOrder.charges?.reduce((sum, item) => {
        const chargeTypeId = item.chargeTypeId ?? item._chargeTypeId;
        const fuelTypeId = item.fuelTypeId ?? item._fuelTypeId;
        const count = item.licensePlates?.length || (item.licensePlate ? 1 : 0) || 1;
        return sum + this.priceService.chargePrice(chargeTypeId, fuelTypeId) * count;
      }, 0) || 0
    );
  }

  @computed
  get vehicleInfo() {
    if (!this.chargeOrder.charges?.length) return "";
    const item = this.chargeOrder.charges[0];
    if (!item) return "";
    const country = this.enumService.values("countries").find((c: any) => c.id === item.countryId);
    if (!country?.code) return "";
    const plate = item.licensePlates?.length > 0 ? item.licensePlates[0] : item.licensePlate ?? "";
    return `${country.code} – ${plate}`;
  }

  @computed
  get chargeType() {
    if (!this.chargeOrder.charges?.length) return "";
    const item = this.chargeOrder.charges[0];
    let chargeType = this.chargeTypesService.getPriceListItemChargeType(item.priceListItemId);
    if (!chargeType && item._chargeTypeId) {
      chargeType = this.chargeTypesService.findChargeType(item._chargeTypeId);
    }
    if (!chargeType && item.chargeTypeId) {
      chargeType = this.chargeTypesService.findChargeType(item.chargeTypeId);
    }
    return chargeType?.description ?? "";
  }

  @computed
  get isSingleDay() {
    if (!this.chargeOrder.charges?.length) return false;
    const item = this.chargeOrder.charges[0];
    let chargeType = this.chargeTypesService.getPriceListItemChargeType(item.priceListItemId);
    if (!chargeType && item._chargeTypeId) {
      chargeType = this.chargeTypesService.findChargeType(item._chargeTypeId);
    }
    if (!chargeType && item.chargeTypeId) {
      chargeType = this.chargeTypesService.findChargeType(item.chargeTypeId);
    }
    return chargeType?.validFor === ChargeValidity.SingleDay;
  }

  @computed
  get fuelType() {
    if (!this.chargeOrder.charges?.length) return "";
    const item = this.chargeOrder.charges[0];
    let fuelType = this.chargeTypesService.getPriceListItemFuelType(item.priceListItemId);
    if (!fuelType) {
      const fuelTypeId = item._fuelTypeId ?? item.fuelTypeId;
      if (fuelTypeId) {
        const fuelTypes = this.enumService.values("fuel_types") as { id: string; fuelType: FuelType }[];
        fuelType = fuelTypes?.find(x => x.id === fuelTypeId)?.fuelType;
      }
    }
    return fuelType ? this.localizationService.translateAttribute("fuelType", fuelType) : "";
  }

  @computed
  get validSince() {
    if (!this.chargeOrder.charges?.length) return null;
    return this.chargeOrder.charges[0]?.validity?.validSinceDate ?? null;
  }

  @computed
  get validUntil() {
    if (!this.chargeOrder.charges?.length) return null;
    return this.chargeOrder.charges[0]?.validity?._validUntilDate ?? null;
  }

  @computed
  get email() {
    return this.chargeOrder.emailAddress;
  }

  @computed
  get notificationEmail() {
    const prefs = this.chargeOrder.notificationPreferences;
    if (!prefs?.emailNotificationEnabled) return undefined;
    return prefs._emailAddressNotification ?? prefs.emailAddress;
  }

  @computed
  get notificationPhone() {
    const prefs = this.chargeOrder.notificationPreferences;
    if (!prefs?.smsNotificationEnabled) return undefined;
    return prefs.phoneNumber;
  }

  @computed
  get singlePrice() {
    if (!this.chargeOrder.charges?.length) return "";
    const item = this.chargeOrder.charges[0];
    const chargeTypeId = item.chargeTypeId ?? item._chargeTypeId;
    const fuelTypeId = item.fuelTypeId ?? item._fuelTypeId;
    const count = item.licensePlates?.length || (item.licensePlate ? 1 : 0) || 1;
    const price = this.priceService.chargePrice(chargeTypeId, fuelTypeId) * count;
    return this.localizationService.formatPrice(price);
  }

  @computed
  get grandTotal() {
    const total = this.priceService.grandTotal(this.chargeOrder);
    return this.localizationService.formatPrice(total);
  }

  @computed
  get grandTotalRaw() {
    return this.priceService.grandTotal(this.chargeOrder);
  }

  uniqueCharges(): { chargeTypeId: string; fuelTypeId: string; index: number; count: number }[] {
    return this.chargeOrder.charges
      .map((item, index) => ({
        chargeTypeId: item.chargeTypeId ?? item._chargeTypeId ?? "",
        fuelTypeId: item.fuelTypeId ?? item._fuelTypeId ?? "",
        index: index + 1,
        count: item.licensePlates?.length ?? (item.licensePlate ? 1 : 0) ?? 1,
      }))
      .filter(d => d.chargeTypeId && d.fuelTypeId);
  }

  chargeTypeTotal(chargeTypeId: string, fuelTypeId: string, count: number) {
    const price = this.priceService.chargePrice(chargeTypeId, fuelTypeId) * count;
    return this.localizationService.formatPrice(price);
  }

  chargeTypeLabel(chargeTypeId: string) {
    const chargeType = this.chargeTypesService.findChargeType(chargeTypeId);
    if (!chargeType) return "";
    return this.localizationService.translateAttribute("chargeType", chargeType.validFor);
  }

  vehicleCountLabel(index: number) {
    const template = this.localizationService.translateGeneral(`eshop.payment.retry.setLabel`);
    return template.replace(/%index%/g, index.toString());
  }

  pluralizeVehicles(count: number) {
    const tr = (code: string) => this.localizationService.translateGeneral(`eshop.payment.retry.${code}`);
    let key = "vehicleCount";
    if (count === 1) {
      key = "vehicleCount1";
    } else if (count >= 2 && count <= 4) {
      key = "vehicleCount234";
    }
    return tr(key).replace(/%count%/g, count.toString());
  }

  @action.bound
  async fetchPayGatePayment() {
    if (!this.chargeOrder.id) return;
    this.isLoading = true;
    try {
      const response = await this.chargeOrderRepository.getPayGatePayment(this.chargeOrder.id);
      this.payGatePaymentResponse = response;
      if (response?.status === PayGatePaymentStatus.SUCCEEDED) {
        this.onPaymentSucceeded();
      } else if (response?.status === PayGatePaymentStatus.IN_PROGRESS) {
        this.onPaymentInProgress();
      }
    } catch {
      this.payGatePaymentResponse = undefined;
    } finally {
      this.isLoading = false;
    }
  }

  @action.bound
  private async ensureGooglePayMetadata() {
    if (this.googlePayMetadata) return;
    await this.paymentService.getGooglePayMetadata();
  }

  @action.bound
  private receivedGooglePayMetadata(data: { type: string; payload: { googlePayMetadata: GooglePayMetadata } }) {
    this.googlePayMetadata = data.payload.googlePayMetadata;
  }

  @action.bound
  private googlePayMetadataFetchFailure() {
    this.googlePayMetadata = undefined;
  }

  @action.bound
  private googlePayPaymentBrowserFingerprint(event: { payload: { url: string } }) {
    this.googlePayPaymentFingerprint = event.payload.url;
    this.googlePayFingerprintProcessed = false;
  }

  @action.bound
  private googlePayFingerprintReset() {
    this.googlePayPaymentFingerprint = undefined;
    this.googlePayFingerprintProcessed = false;
  }

  @action.bound
  private onSubmitGooglePayPaymentSuccess() {
    this.googlePayPaymentFingerprint = undefined;
    this.googlePayFingerprintProcessed = false;
    this.isLoading = false;
    this.onPaymentSucceeded();
  }

  @action.bound
  private onSubmitGooglePayPaymentDeferred() {
    this.googlePayPaymentFingerprint = undefined;
    this.googlePayFingerprintProcessed = false;
    this.isLoading = false;
    this.onPaymentInProgress();
  }

  @action.bound
  onGooglePayPaymentFingerprintLoaded = () => {
    if (!this.googlePayFingerprintProcessed) {
      this.googlePayFingerprintProcessed = true;
      this.eventBus.publish(googlePayPaymentBrowserFingerprintLoaded());
    }
  };

  @action.bound
  async handleRetryPayment() {
    if (!this.chargeOrder.id) return;
    this.isLoading = true;
    try {
      const response = await this.chargeOrderRepository.getPayGatePayment(this.chargeOrder.id);
      this.payGatePaymentResponse = response;

      if (response?.permittedActions?.paymentRetry?.isAllowed) {
        this.onRetryPayment();
      } else if (response?.status === PayGatePaymentStatus.SUCCEEDED) {
        this.onPaymentSucceeded();
      } else if (response?.status === PayGatePaymentStatus.IN_PROGRESS) {
        this.onPaymentInProgress();
      } else {
        this.retryBlocked = true;
      }
    } catch {
      this.payGatePaymentResponse = undefined;
    } finally {
      this.isLoading = false;
    }
  }

  @action.bound
  async handleRetryGooglePayPayment(paymentData: google.payments.api.PaymentData) {
    if (!this.chargeOrder.id || !this.onRetryGooglePayPayment) return;
    this.isLoading = true;
    try {
      const response = await this.chargeOrderRepository.getPayGatePayment(this.chargeOrder.id);
      this.payGatePaymentResponse = response;

      if (!response?.permittedActions?.paymentRetry?.isAllowed) {
        if (response?.status === PayGatePaymentStatus.SUCCEEDED) {
          this.onPaymentSucceeded();
        } else if (response?.status === PayGatePaymentStatus.IN_PROGRESS) {
          this.onPaymentInProgress();
        } else {
          this.retryBlocked = true;
        }
        return;
      }

      await this.onRetryGooglePayPayment(paymentData);
    } catch {
      this.payGatePaymentResponse = undefined;
    } finally {
      this.isLoading = false;
    }
  }

  @action.bound
  validateForGooglePay() {
    return !!this.chargeOrder.id && this.isGooglePayAllowed;
  }

  @action.bound
  handleBackToPurchase() {
    this.onBackToPurchase();
  }

  @action.bound
  closeIdleModal() {
    this.showIdleModal = false;
    this.clearTimeoutInterval();
  }

  @action.bound
  onIdleTimeout() {
    this.showIdleModal = false;
    this.clearTimeoutInterval();
    if (this.onIdleTimeoutCallback) {
      this.onIdleTimeoutCallback();
    } else {
      window.location.href = this.localizationService.translateGeneral("eshop.idle.timeoutRedirect", undefined, true);
    }
  }

  @action.bound
  private clearTimeoutInterval() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
  }

  @action.bound
  async handleOnIdle() {
    if (this.showIdleModal || this.isLoading) return;

    this.counter = this.IDLE_PROMPT_TIMEOUT;
    this.showIdleModal = true;
    this.intervalId = setInterval(async () => {
      this.counter = this.counter - 1000;
      if (this.counter === 0) {
        this.onIdleTimeout();
      }
    }, 1000);
  }

  static Factory({ container }: interfaces.Context) {
    return (
      chargeOrder: ChargeOrder,
      onRetryPayment: () => void,
      onBackToPurchase: () => void,
      onPaymentSucceeded: () => void,
      onPaymentInProgress: () => void,
      onRetryGooglePayPayment?: (paymentData: google.payments.api.PaymentData) => Promise<void> | void,
      onIdleTimeoutCallback?: () => void
    ) =>
      new PaymentRetryViewModel(
        chargeOrder,
        container.get(LocalizationService),
        container.get(TimeService),
        container.get(ChargeOrderRepository),
        container.get(ChargeTypesService),
        container.get(EnumService),
        container.get(PriceService),
        container.get(PaymentService),
        container.get(EventBus),
        onRetryPayment,
        onBackToPurchase,
        onPaymentSucceeded,
        onPaymentInProgress,
        onRetryGooglePayPayment,
        onIdleTimeoutCallback
      );
  }
}
